import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { fetchActivePromoImages } from "../api/promosApi";
import {
  getAvailableSubscriptionPlans,
  getMySubscriptions,
  cancelMySubscription,
} from "../api/subscriptionApi";
import PromoCarousel from "../components/PromoCarousel";
import "./css/AbbonamentiCliente.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const fallbackSlides = [
  { img: "/images/03-hero-promo-benvenuto.png", title: "Promo benvenuto" },
  { img: "/images/03-hero_02.png", title: "Stirup" },
  { img: "/images/03-hero_03.png", title: "Stirup" },
];

const euro = (cents) => {
  const value = Number(cents);
  if (!Number.isFinite(value)) return "—";

  return (value / 100).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

function getPresentation(plan) {
  return plan?.config?.presentation || {};
}

function getPlanTitle(plan) {
  return getPresentation(plan).title || plan.name || "Abbonamento";
}

function getPlanShort(plan) {
  return getPresentation(plan).short_description || plan.description || "";
}

function getPlanColor(plan, fallback = "#0877d9") {
  return getPresentation(plan).theme_color || fallback;
}

// Riconosce la riga "prezzo" tra quelle configurate in admin (icona o label),
// per poterla escludere: il prezzo in card è SEMPRE ricalcolato qui sotto,
// mai preso dal testo statico salvato in admin (che è mensile e non si
// aggiorna da solo se il prezzo cambia).
function isPriceRow(row) {
  return (
    row.icon === "💶" ||
    row.icon === "🏷️" ||
    /costo|prezzo/i.test(row.label || "")
  );
}

function getPlanRows(plan) {
  const configuredRows = getPresentation(plan).rows;

  const featureRows =
    Array.isArray(configuredRows) && configuredRows.length
      ? configuredRows
          .map((r) => ({
            icon: r.icon || "•",
            label: r.label || "",
            value: r.value || "",
          }))
          .filter((r) => (r.icon || r.label || r.value) && !isPriceRow(r))
      : [
          {
            icon: "👕",
            label: "N° capi max",
            value:
              plan?.config?.max_items != null
                ? String(plan.config.max_items)
                : "—",
          },
        ];

  return [
    ...featureRows,
    {
      icon: "🏷️",
      label: "Costo a settimana",
      // SOLO VISIVO: l'addebito reale resta mensile (plan.price_cents,
      // invariato, riletto identico in AbbonamentoAttiva/checkout). Qui si
      // formatta soltanto una stima settimanale indicativa per la card,
      // senza persistere né propagare il valore diviso.
      value: euro(plan.price_cents / 4),
    },
  ];
}

function imageForPlan(plan) {
  const key = getPresentation(plan).image_key;

  if (key === "folded-small") return "/images/folded-small.png";
  if (key === "folded-medium") return "/images/folded-medium.png";
  if (key === "folded-large") return "/images/folded-large.png";

  return plan.image_url || "/images/folded-small.png";
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("it-IT");
}

export default function AbbonamentiCliente() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const [slides, setSlides] = useState(fallbackSlides);
  const [plans, setPlans] = useState([]);
  const [mySubscriptions, setMySubscriptions] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [loadingMine, setLoadingMine] = useState(true);
  const [busySubId, setBusySubId] = useState(null);

  const visiblePlans = useMemo(() => {
    return plans;
  }, [plans]);

  const hasPlans = visiblePlans.length > 0;

  async function loadPlans() {
    if (!token) return;

    setLoadingPlans(true);
    try {
      const data = await getAvailableSubscriptionPlans(token);
      setPlans(Array.isArray(data?.plans) ? data.plans : []);
    } catch (err) {
      toast.error(err.message || "Errore caricamento abbonamenti");
      setPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  }

  async function loadMine() {
    if (!token) return;

    setLoadingMine(true);
    try {
      const data = await getMySubscriptions(token);
      setMySubscriptions(
        Array.isArray(data?.subscriptions) ? data.subscriptions : [],
      );
    } catch (err) {
      toast.error(err.message || "Errore caricamento tuoi abbonamenti");
    } finally {
      setLoadingMine(false);
    }
  }

  useEffect(() => {
    loadPlans();
    loadMine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function loadSlides() {
      try {
        const promos = await fetchActivePromoImages({
          token,
          limit: 6,
        });

        const mapped = Array.isArray(promos)
          ? promos
              .filter((p) => p.image_url)
              .map((p) => ({
                id: p.id,
                img: p.image_url,
                title: p.code ? `Promo ${p.code}` : "Promo",
              }))
          : [];

        if (mapped.length) setSlides(mapped);
      } catch {
        setSlides(fallbackSlides);
      }
    }

    loadSlides();
  }, [token]);

  async function handleCancel(sub) {
    const ok = window.confirm(
      "Vuoi disattivare il rinnovo di questo abbonamento a fine periodo?",
    );

    if (!ok) return;

    setBusySubId(sub.id);
    try {
      await cancelMySubscription(sub.id, token);
      toast.success("Rinnovo disattivato");
      await loadMine();
    } catch (err) {
      toast.error(err.message || "Errore cancellazione abbonamento");
    } finally {
      setBusySubId(null);
    }
  }

  return (
    <main className="cap-page">
      <div className="cap-page-wrapper">
        <PromoCarousel slides={slides} />

        <section className="cap-content">
          {/* HERO — copia fedele da antipanico-confronto.html */}
          <section className="plans-hero" aria-labelledby="plans-hero-title">
            <div className="plans-flags" aria-hidden="true">
              <span className="plans-flag" />
              <span className="plans-flag" />
              <span className="plans-flag" />
              <span className="plans-flag" />
              <span className="plans-flag" />
              <span className="plans-flag" />
              <span className="plans-flag" />
            </div>

            <div className="plans-hero-copy">
              <div className="plans-kicker">✧ Scegli la tua misura</div>
              <h1 id="plans-hero-title" className="plans-title">
                Calendario
                <span>anti panico</span>
              </h1>
              <p className="plans-subtitle">
                Scegli il taglio più adatto alla quantità di capi che vuoi
                gestire ogni mese: Small, Medium o Large. Un ritiro fisso,
                meno pensieri e una routine finalmente decente, evento raro
                ma non impossibile. <br />
                Sul calendario anti panico non si paga il trasporto!
              </p>
            </div>

            <div className="plans-hero-visual" aria-hidden="true">
              <img
                className="plans-hero-woman"
                src="/images/calendario-anti-panico-hero.png"
                alt=""
              />

              <div className="plans-calendar">
                <div className="plans-calendar-head">La tua settimana</div>
                <div className="plans-calendar-week">
                  <span>LUN</span>
                  <span>MAR</span>
                  <span>MER</span>
                  <span>GIO</span>
                  <span>VEN</span>
                  <span>SAB</span>
                  <span>DOM</span>
                </div>
                <div className="plans-calendar-days">
                  {Array.from({ length: 21 }).map((_, i) => (
                    <span key={i} className={i === 10 ? "marked" : ""} />
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="cap-benefits">
            <article className="cap-benefit">
              <div className="cap-icon">📅</div>
              <div>
                <h3>Giorno fisso</h3>
                <p>Imposti una routine e non ci pensi più.</p>
              </div>
            </article>

            <article className="cap-benefit">
              <div className="cap-icon">👕</div>
              <div>
                <h3>Capi sotto controllo</h3>
                <p>Ogni piano ha limiti chiari e configurabili.</p>
              </div>
            </article>

            <article className="cap-benefit">
              <div className="cap-icon">🚚</div>
              <div>
                <h3>Ritiro a domicilio</h3>
                <p>Scegli il fornitore nella fase di attivazione.</p>
              </div>
            </article>
          </section>

          <section className="cap-soft">
            <h2 className="cap-section-title">Piani disponibili</h2>

            {loadingPlans ? (
              <div className="cap-empty">Caricamento abbonamenti...</div>
            ) : !hasPlans ? (
              <div className="cap-empty">
                Al momento non ci sono abbonamenti disponibili.
              </div>
            ) : (
              <>
                <section
                  className="plans-compare plans-compare--home"
                  aria-label="Piani abbonamento disponibili"
                >
                  {visiblePlans.slice(0, 3).map((plan) => {
                    const color = getPlanColor(plan);
                    const rows = getPlanRows(plan);
                    const cta =
                      getPresentation(plan).cta_label || "Scopri il piano";

                    return (
                      <article
                        className="plan-card"
                        key={plan.id}
                        style={{
                          "--plan-color": color,
                        }}
                      >
                        <img
                          className="plan-card-img"
                          src={imageForPlan(plan)}
                          alt=""
                          aria-hidden="true"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />

                        <h2 className="plan-name">{getPlanTitle(plan)}</h2>

                        <p className="plan-short">{getPlanShort(plan)}</p>

                        <div className="plan-table">
                          {rows.map((row, idx) => (
                            <div
                              className="plan-row"
                              key={`${plan.id}-row-${idx}`}
                            >
                              <span className="plan-icon">
                                {row.icon || "•"}
                              </span>
                              <span>{row.label}</span>
                              <strong className="plan-value">
                                {row.value}
                              </strong>
                            </div>
                          ))}
                        </div>

                        <button
                          type="button"
                          className="plan-cta"
                          onClick={() => navigate(`/abbonamenti/attiva/${plan.id}`)}
                        >
                          {cta}
                          <span aria-hidden="true">›</span>
                        </button>
                      </article>
                    );
                  })}
                </section>

                <p className="cap-note">
                  <span>i</span>I piani mostrati saranno attivabili solo se
                  almeno un fornitore ha aderito.
                </p>
              </>
            )}
          </section>

          {/* CONDIZIONI DEL SERVIZIO — copia fedele da antipanico-confronto.html */}
          <section
            id="condizioni-servizio"
            className="conditions-box"
            aria-labelledby="conditions-title"
          >
            <div className="conditions-title">
              <div className="conditions-icon">📋</div>
              <h2 id="conditions-title">
                Condizioni
                <br />
                del servizio
              </h2>
            </div>

            <div>
              <ul className="conditions-list">
                <li>
                  Il numero indicato è il massimo di capi che non può essere
                  superato per ogni consegna.
                </li>
                <li>
                  Se consegni meno capi rispetto alla misura scelta, non sono
                  previsti sconti o rimborsi.
                </li>
                <li>
                  Devono essere rispettati i numeri massimi dei capi
                  speciali, ad esempio camicie o lenzuola.
                </li>
                <li>
                  Eventuali capi extra, anche per tipologia, vengono
                  addebitati separatamente.
                </li>
                <li>
                  L’abbonamento può essere sospeso in qualsiasi momento. La
                  sospensione ha effetto dal mese successivo.
                </li>
                <li>
                  Il mese in corso è comunque già stato addebitato e non
                  viene rimborsato.
                </li>
                <li>L'abbonamento è MENSILE</li>
                <li>
                  In caso di variazione dei prezzi o condizioni degli
                  abbonamenti il cliente sarà libero di annullare
                  l'abbonamento prima del rinnovo
                </li>
              </ul>
              <p className="conditions-note">
                Le condizioni economiche e operative possono essere
                aggiornate nel tempo. Quelle valide sono sempre quelle
                mostrate al momento dell’attivazione o della modifica del
                servizio.
              </p>
            </div>
          </section>

          {/* COME FUNZIONA — stesso testo di prima, restilizzato come "anti-panic-steps" della pagina web */}
          <section
            className="anti-panic-steps"
            aria-label="Come funziona il Calendario Anti Panico"
          >
            <article className="anti-panic-step">
              <div className="anti-panic-step-number" aria-hidden="true">
                1
              </div>
              <h2>Scegli il piano</h2>
              <p>Confronti i piani disponibili nella tua zona.</p>
            </article>

            <article className="anti-panic-step">
              <div className="anti-panic-step-number" aria-hidden="true">
                2
              </div>
              <h2>Scegli il fornitore</h2>
              <p>Lo fai solo dopo aver scelto il piano.</p>
            </article>

            <article className="anti-panic-step">
              <div className="anti-panic-step-number" aria-hidden="true">
                3
              </div>
              <h2>Fissi il giorno</h2>
              <p>Il ritiro segue le fasce disponibili del fornitore.</p>
            </article>

            <article className="anti-panic-step">
              <div className="anti-panic-step-number" aria-hidden="true">
                4
              </div>
              <h2>Ti liberi dello stiro</h2>
              <p>Finalmente, una cosa ragionevole in mezzo al caos.</p>
            </article>
          </section>

          <section className="cap-closing">
            <div className="cap-closing-img" aria-hidden />
            <div className="cap-closing-copy">
              <h2>Lo stiro non deve occuparti la testa.</h2>
              <p>
                Scegli un piano ricorrente, imposta giorno e fornitore, e lascia
                che il calendario lavori al posto tuo.
              </p>
            </div>
          </section>

          <section className="abb-client-card abb-client-card--mine">
            <div className="abb-client-section-head">
              <div>
                <h2>I miei abbonamenti</h2>
              </div>
            </div>

            {loadingMine ? (
              <div className="abb-client-empty">Caricamento abbonamenti...</div>
            ) : mySubscriptions.length === 0 ? (
              <div className="abb-client-empty">
                Non hai ancora abbonamenti attivi.
              </div>
            ) : (
              <div className="abb-client-table-wrap">
                <table className="abb-client-table">
                  <thead>
                    <tr>
                      <th>Piano</th>
                      <th>Fornitore</th>
                      <th>Stato</th>
                      <th>Fine periodo</th>
                      <th>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mySubscriptions.map((sub) => {
                      const canCancel = ["active", "paused"].includes(
                        sub.status,
                      );

                      return (
                        <tr key={sub.id}>
                          <td>
                            <strong>{sub.plan_name}</strong>
                            <div className="abb-client-muted">
                              {euro(sub.price_cents)} / mese
                            </div>
                          </td>
                          <td>{sub.supplier_name || `#${sub.supplier_id}`}</td>
                          <td>{sub.status}</td>
                          <td>{formatDate(sub.current_period_end)}</td>
                          <td>
                            {canCancel ? (
                              <button
                                type="button"
                                className="abb-client-small-btn"
                                disabled={busySubId === sub.id}
                                onClick={() => handleCancel(sub)}
                              >
                                {busySubId === sub.id
                                  ? "..."
                                  : "Disattiva rinnovo"}
                              </button>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </section>
      </div>
    </main>
  );
}
